import React from 'react';
import { Button } from '@/components/ui/button';
import { LucideIcon } from 'lucide-react';
import { cn } from '@/lib/utils';

type Props = {
    onClick: () => void;
    buttonString: string;
    Icon?: LucideIcon;
    buttonWidth?: string;
    disabled?: boolean;
    variant?: 'default' | 'outline' | 'secondary' | 'ghost';
}

export default function BulkButton({
    onClick,
    buttonString,
    Icon,
    buttonWidth,
    disabled,
    variant = 'default',
}: Props) {
    return (
        <Button
            onClick={onClick}
            disabled={disabled}
            variant={variant}
            size="lg"
            className={cn(
                "h-11 px-6",
                "rounded-lg",
                "font-medium text-sm",
                "whitespace-nowrap",
                "transition-all duration-200",
                variant === 'default' && [
                    "shadow-sm hover:shadow-md",
                    "!border-0",
                ],
                variant === 'outline' && "border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-800 text-slate-900 dark:text-slate-50 hover:bg-slate-50 dark:hover:bg-slate-700",
                variant === 'secondary' && "bg-slate-100 dark:bg-slate-800 text-slate-900 dark:text-slate-50 hover:bg-slate-200 dark:hover:bg-slate-700",
                variant === 'ghost' && "hover:bg-slate-100 dark:hover:bg-slate-800 text-slate-900 dark:text-slate-50"
            )}
            // The brand gradient and its paired ink come from the palette, not from
            // literals here. This button used to inline the sky gradient AND force
            // `!text-white` on top of it, so it sat outside the token system entirely:
            // the paired-ink fix that gave --fab-bg a legible label never reached it,
            // and white on that gradient measures 3.05:1 at the deep stop falling to
            // 1.58:1 at the light one — in BOTH themes, because the gradient is the
            // same in both. --fab-text is the ink the palette already pairs with it.
            style={variant === 'default' ? {
                width: buttonWidth || 'auto',
                background: "var(--fab-bg)",
                color: "var(--fab-text)",
            } : { width: buttonWidth || 'auto' }}
        >
            {Icon && <Icon className="h-4 w-4" />}
            {buttonString}
        </Button>
    )
}